import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useUser } from "@clerk/clerk-react";

const SIDEBAR_THREAD_ID_KEY = "manageclean-sidebar-thread-id";
const SIDEBAR_CANVAS_EXPANDED_KEY = "manageclean-sidebar-canvas-expanded";
const SIDEBAR_THREAD_DOCUMENTS_KEY = "manageclean-sidebar-thread-documents";

interface AgentSidebarContextType {
  // Sidebar visibility
  isVisible: boolean;
  isMinimized: boolean;
  threadId: string | null;
  resolutionMode: {
    active: boolean;
    protocolId?: string;
    contextData?: any;
  };

  // Canvas expansion state
  isCanvasExpanded: boolean;
  activeDocumentId: string | null;

  // Sidebar controls
  showSidebar: () => void;
  hideSidebar: () => void;
  toggleSidebar: () => void;
  minimizeSidebar: () => void;
  expandSidebar: () => void;
  toggleMinimize: () => void;
  setThreadId: (threadId: string | null) => void;
  clearThread: () => void;
  enterResolutionMode: (protocolId: string, contextData: any) => void;
  exitResolutionMode: () => void;

  // Canvas controls
  expandCanvas: (documentId: string) => void;
  collapseCanvas: () => void;
  setActiveDocument: (documentId: string | null) => void;
  getThreadDocument: (threadId: string) => string | null;
}

const AgentSidebarContext = createContext<AgentSidebarContextType | null>(null);

export function useAgentSidebar() {
  const context = useContext(AgentSidebarContext);
  if (!context) {
    throw new Error("useAgentSidebar must be used within an AgentSidebarProvider");
  }
  return context;
}

interface AgentSidebarProviderProps {
  children: ReactNode;
}

export function AgentSidebarProvider({ children }: AgentSidebarProviderProps) {
  const { user, isLoaded } = useUser();
  const [isVisible, setIsVisible] = useState(true); // Default to visible
  const [isMinimized, setIsMinimized] = useState(false);

  // Persist threadId across component remounts and page reloads
  const [threadId, setThreadIdState] = useState<string | null>(null);
  const [resolutionMode, setResolutionMode] = useState<{
    active: boolean;
    protocolId?: string;
    contextData?: any;
  }>({ active: false });

  // Load user-specific threadId when user loads
  useEffect(() => {
    if (isLoaded) {
      if (user) {
        const key = `${SIDEBAR_THREAD_ID_KEY}:${user.id}`;
        const stored = localStorage.getItem(key);
        setThreadIdState(stored || null);
      } else {
        // Clear state on logout
        setThreadIdState(null);
      }
    }
  }, [user, isLoaded]);

  // Canvas expansion state - persist to localStorage
  const [isCanvasExpanded, setIsCanvasExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_CANVAS_EXPANDED_KEY);
      return stored === "true";
    } catch (error) {
      console.error("Failed to load canvas state from localStorage:", error);
      return false;
    }
  });

  const [activeDocumentId, setActiveDocumentIdState] = useState<string | null>(null);

  // Map of threadId -> documentId for quick lookup - persist to localStorage
  const [threadDocuments, setThreadDocuments] = useState<Map<string, string>>(new Map());

  // Load user-specific thread documents when user loads
  useEffect(() => {
    if (isLoaded && user) {
      try {
        const key = `${SIDEBAR_THREAD_DOCUMENTS_KEY}:${user.id}`;
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored);
          setThreadDocuments(new Map(Object.entries(parsed)));
        } else {
          setThreadDocuments(new Map());
        }
      } catch (error) {
        console.error("Failed to load thread documents from localStorage:", error);
        setThreadDocuments(new Map());
      }
    } else if (isLoaded && !user) {
      setThreadDocuments(new Map());
    }
  }, [user, isLoaded]);

  // Persist threadId to localStorage whenever it changes
  useEffect(() => {
    if (!isLoaded || !user) return;
    try {
      const key = `${SIDEBAR_THREAD_ID_KEY}:${user.id}`;
      if (threadId) {
        localStorage.setItem(key, threadId);
      } else {
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error("Failed to save threadId to localStorage:", error);
    }
  }, [threadId, user, isLoaded]);

  // Persist canvas expanded state
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_CANVAS_EXPANDED_KEY, String(isCanvasExpanded));
    } catch (error) {
      console.error("Failed to save canvas state to localStorage:", error);
    }
  }, [isCanvasExpanded]);

  // Persist thread documents map
  useEffect(() => {
    if (!isLoaded || !user) return;
    try {
      const key = `${SIDEBAR_THREAD_DOCUMENTS_KEY}:${user.id}`;
      const obj = Object.fromEntries(threadDocuments);
      localStorage.setItem(key, JSON.stringify(obj));
    } catch (error) {
      console.error("Failed to save thread documents to localStorage:", error);
    }
  }, [threadDocuments, user, isLoaded]);

  const showSidebar = () => {
    setIsVisible(true);
    setIsMinimized(false);
  };
  const hideSidebar = () => setIsVisible(false);
  const toggleSidebar = () => {
    setIsVisible((prev) => {
      if (!prev) {
        setIsMinimized(false);
        return true;
      }
      return false;
    });
  };

  const minimizeSidebar = () => setIsMinimized(true);
  const expandSidebar = () => setIsMinimized(false);
  const toggleMinimize = () => setIsMinimized((prev) => !prev);

  const setThreadId = (newThreadId: string | null) => {
    console.log("📌 Setting sidebar threadId:", newThreadId);
    setThreadIdState(newThreadId);
  };

  const clearThread = () => {
    console.log("🗑️ Clearing sidebar thread");
    setThreadIdState(null);
  };

  const enterResolutionMode = (protocolId: string, contextData: any) => {
    console.log("🎯 Entering Resolution Mode:", protocolId);
    setResolutionMode({ active: true, protocolId, contextData });
    setIsVisible(true);
    setIsMinimized(false);
  };

  const exitResolutionMode = () => {
    console.log("🚪 Exiting Resolution Mode");
    setResolutionMode({ active: false });
  };

  // Canvas control methods
  const expandCanvas = (documentId: string) => {
    console.log("🎨 Expanding canvas with document:", documentId);
    setActiveDocumentIdState(documentId);
    setIsCanvasExpanded(true);

    // Associate document with current thread
    if (threadId) {
      setThreadDocuments((prev) => {
        const newMap = new Map(prev);
        newMap.set(threadId, documentId);
        return newMap;
      });
    }
  };

  const collapseCanvas = () => {
    console.log("🎨 Collapsing canvas");
    setIsCanvasExpanded(false);
    // Keep activeDocumentId so we can re-expand to same document
  };

  const setActiveDocument = (documentId: string | null) => {
    console.log("📄 Setting active document:", documentId);
    setActiveDocumentIdState(documentId);
    if (documentId) {
      setIsCanvasExpanded(true);

      // Associate with current thread
      if (threadId) {
        setThreadDocuments((prev) => {
          const newMap = new Map(prev);
          newMap.set(threadId, documentId);
          return newMap;
        });
      }
    }
  };

  const getThreadDocument = (threadId: string): string | null => {
    return threadDocuments.get(threadId) || null;
  };

  const contextValue: AgentSidebarContextType = {
    isVisible,
    isMinimized,
    threadId,
    isCanvasExpanded,
    activeDocumentId,
    resolutionMode,
    showSidebar,
    hideSidebar,
    toggleSidebar,
    minimizeSidebar,
    expandSidebar,
    toggleMinimize,
    setThreadId,
    clearThread,
    enterResolutionMode,
    exitResolutionMode,
    expandCanvas,
    collapseCanvas,
    setActiveDocument,
    getThreadDocument,
  };

  return (
    <AgentSidebarContext.Provider value={contextValue}>{children}</AgentSidebarContext.Provider>
  );
}
